import dayjs from "dayjs";
import moment from "moment";
import { UiControlType } from "../Types/UiControlType";

/**
 * Centralized table sorting helpers to avoid duplicated sorting logic across components
 */

/**
 * Sort strings alphabetically with locale support
 */
export const sortAlphabetically = (a: any, b: any, propName?: string, locale: string = 'sv'): number => {
    let aValue: string;
    let bValue: string;

    if (propName) {
        aValue = a[propName] != null && a[propName] != undefined ? a[propName].toString().toLowerCase() : "";
        bValue = b[propName] != null && b[propName] != undefined ? b[propName].toString().toLowerCase() : "";
    } else {
        aValue = a != null && a != undefined ? a.toString().toLowerCase() : "";
        bValue = b != null && b != undefined ? b.toString().toLowerCase() : "";
    }

    return aValue.localeCompare(bValue, locale);
};

/**
 * Sort dates
 */
export const sortDate = (a: Date | string | undefined | null, b: Date | string | undefined | null): number => {
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    return moment(a).diff(moment(b));
};

/**
 * Sort numbers
 */
export const sortNumber = (a: number | string | undefined | null, b: number | string | undefined | null): number => {
    const aNum = a != null ? Number(a) : 0;
    const bNum = b != null ? Number(b) : 0;

    if (isNaN(aNum) && isNaN(bNum)) return 0;
    if (isNaN(aNum)) return -1;
    if (isNaN(bNum)) return 1;

    return aNum - bNum;
};

/**
 * Sort booleans (useful for YesNo fields)
 */
export const sortBoolean = (a: boolean | string | undefined | null, b: boolean | string | undefined | null): number => {
    const aVal = a === true || a === "true" ? 1 : 0;
    const bVal = b === true || b === "true" ? 1 : 0;
    return aVal - bVal;
};

/**
 * Generic sorter for custom fields based on UiControlType
 */
export const sortByUiControlType = (
    uiControl: UiControlType | undefined,
    aValue: any,
    bValue: any,
    lookupOptions?: Array<{ value: any; label: string }>
): number => {
    // Handle null/undefined values
    if (aValue === null || aValue === undefined || aValue === '') return 1;
    if (bValue === null || bValue === undefined || bValue === '') return -1;

    switch (uiControl) {
        case UiControlType.Date:
            const aDate = dayjs(aValue).valueOf() || 0;
            const bDate = dayjs(bValue).valueOf() || 0;
            return aDate - bDate;

        case UiControlType.YesNo:
            return sortBoolean(aValue, bValue);

        case UiControlType.Number:
        case UiControlType.Currency:
        case UiControlType.Decimal:
            return sortNumber(aValue, bValue);

        case UiControlType.Lookup:
            if (lookupOptions) {
                const aLabel = lookupOptions.find((opt: any) => String(opt.value) === String(aValue))?.label || '';
                const bLabel = lookupOptions.find((opt: any) => String(opt.value) === String(bValue))?.label || '';
                return aLabel.localeCompare(bLabel);
            }
            return String(aValue).localeCompare(String(bValue));

        case UiControlType.Text:
        case UiControlType.Dropdown:
        default:
            // Try numeric comparison first
            const aNum = Number(aValue);
            const bNum = Number(bValue);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }
            // Fall back to string comparison
            return String(aValue).localeCompare(String(bValue));
    }
};

/**
 * Create a sorter function for Ant Design table columns
 */
export const createColumnSorter = <T = any>(
    getValue: (record: T) => any,
    uiControl?: UiControlType,
    lookupOptions?: Array<{ value: any; label: string }>
) => {
    return (a: T, b: T) => {
        const aValue = getValue(a);
        const bValue = getValue(b);

        if (uiControl !== undefined) {
            return sortByUiControlType(uiControl, aValue, bValue, lookupOptions);
        }

        // Default sorting logic
        if (aValue === null || aValue === undefined || aValue === '') return 1;
        if (bValue === null || bValue === undefined || bValue === '') return -1;

        // Try numeric comparison
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
        }

        // String comparison
        return String(aValue).localeCompare(String(bValue));
    };
};
